import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { PageTourButton } from './PageTour';
import { resolveTourLang } from '../tours/studyTours';
import { ALL_TOURS, TourKey } from '../tours/allTours';

/**
 * Cabeçalho padrão das páginas de Estudo (design minimal v1):
 * título editorial com ponto de destaque, UMA frase breve (no idioma do usuário) e o botão "?" do tour.
 * `actions` mostra os botões próprios da página ao lado do "?".
 */
export const StudyPageHeader: React.FC<{
  tourKey: TourKey;
  title: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  /** Esconde o "?" da página (ex.: /settings tem um tour por seção). */
  hideTour?: boolean;
}> = ({ tourKey, title, actions, className = '', hideTour = false }) => {
  const { i18n } = useTranslation();
  const lang = resolveTourLang(i18n.language);
  const description = ALL_TOURS[tourKey].description[lang];

  return (
    <motion.header
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`flex items-start justify-between gap-4 ${className}`}
    >
      <div data-tour="page-title" className="min-w-0">
        <h1 className="font-display text-[38px] md:text-5xl font-light tracking-[-0.045em] leading-[1] text-ink">
          {title}<span className="text-primary">.</span>
        </h1>
        <p className="mt-2.5 text-sm md:text-[15px] text-muted leading-relaxed max-w-md">{description}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0 pt-1">
        {actions}
        {!hideTour && <PageTourButton tourKey={tourKey} />}
      </div>
    </motion.header>
  );
};

/** Só a frase breve da página (para cabeçalhos compactos, ex.: Gramática). */
export const StudyPageDescription: React.FC<{ tourKey: TourKey; className?: string }> = ({ tourKey, className = '' }) => {
  const { i18n } = useTranslation();
  return <span className={className}>{ALL_TOURS[tourKey].description[resolveTourLang(i18n.language)]}</span>;
};

export default StudyPageHeader;
